/*
 *  Product Thumbnail
 *    Script for the product thumbnail
 *
 *  Version:
 *    1.0.0 - 2020/11/22
 */
import {
  ON_CART_FINISHED,
  cartAdd,
  getCountOfVariantInCart,
  getCurrentCart,
} from '@process-creative/slate-theme-tools';

import {
  CLASS_DISABLED,
  CLASS_INITIALISED,
  CLASS_PENDING,
  CLASS_VISIBLE,
} from '../../tools/constants/classes';
import { t } from '../../tools/utilities/string-tools';
import { shopifyAjaxErrorGet } from '../../tools/utilities/error';
import { Product } from '../../tools/product/product';
import { findTarget } from '../../tools/utilities/event-tools';

import { UpdatedVariant } from './variant-selector';

// Attributes
const ATTR_VARIANT_ID = 'data-variant-id';

// Selectors
const SELECTOR_THUMBNAIL = '[data-product-thumbnail]';
const SELECTOR_PRODUCT_JSON = '[data-product-json]';
const SELECTOR_ATC_BUTTON = '[data-product-thumbnail-add-btn]';
const SELECTOR_ATC_BUTTON_LABEL = '[data-product-thumbnail-add-btn-label]';
const SELECTOR_CART_COUNT = '[data-cart-count]';

// Language Strings
const STR_ADD = t('products.product.add_to_cart');
const STR_ALL = t('products.product.all_in_cart');
const STR_PENDING = t('products.product.adding');
const STR_ADDED = t('products.product.added_to_cart');
const STR_SOLD_OUT = t('products.product.sold_out');
const STR_ERROR = t('products.product.error');

const COOLDOWN_ADDED = 2000;

// Errors
const ERROR_NO_JSON = `Attempted to load the variant selector, however there is no ${SELECTOR_PRODUCT_JSON} found in this thumbnail.`;
const ERROR_NO_VARIANT_ID = `Thumbnail has no variant id set, no ${ATTR_VARIANT_ID} found on this thumbnail.`;
const ERROR_NO_VARIANT = `Unable to find variant matching %1% on this product. Incorrect variant id may have been set.`;

export class ProductThumbnail {
  container: HTMLElement;
  atcButtons: NodeListOf<HTMLButtonElement> | null;
  pending: boolean;
  disabled: boolean;
  variantId: number | null;
  product: Product;
  variant: UpdatedVariant | undefined;

  constructor(container: HTMLElement) {
    this.container = container;
    this.pending = false;
    this.disabled = false;
    this.variantId = null;

    // Get the product json
    const jsonElement = container.querySelector<HTMLScriptElement>(
      SELECTOR_PRODUCT_JSON
    );
    if (!jsonElement) throw new Error(ERROR_NO_JSON);

    // Set the product object
    this.product = JSON.parse(jsonElement.innerHTML);

    // Set the variant id
    const variantIdString = container.getAttribute(ATTR_VARIANT_ID);
    if (!variantIdString) throw new Error(ERROR_NO_VARIANT_ID);
    this.variantId = parseInt(variantIdString, 10);

    // Set the variant object
    this.variant = this.product.variants.find(
      (vrnt) => vrnt.id === this.variantId
    );
    if (!this.variant)
      throw new Error(ERROR_NO_VARIANT.replace('%1%', variantIdString));

    // Add to cart button
    this.atcButtons = container.querySelectorAll(SELECTOR_ATC_BUTTON);
    if (this.atcButtons) {
      this.atcButtons.forEach((button) => {
        button.addEventListener('click', (evt) => this.addProduct(evt));
      });
    }
  }

  async updateButton(evt: Event) {
    if (!this.variant || !this.variantId) return;

    const atcButton = findTarget(evt, SELECTOR_ATC_BUTTON);
    if (!atcButton) return;

    const atcButtonLabel = atcButton.querySelector<HTMLElement>(
      SELECTOR_ATC_BUTTON_LABEL
    );
    if (!atcButtonLabel) return;

    const {
      available,
      inventory_policy,
      inventory_management,
      inventory_quantity,
    } = this.variant;
    const inCart = getCountOfVariantInCart(this.variantId);

    if (available && inventory_quantity) {
      if (
        inventory_policy !== 'continue' &&
        inventory_management != null &&
        inventory_quantity - inCart <= 0
      ) {
        this.disabled = true;
        atcButton.classList.add(CLASS_DISABLED);
        atcButton.setAttribute('disabled', 'disabled');
        atcButtonLabel.innerText = STR_ALL;
      } else {
        this.disabled = false;
        atcButton.classList.remove(CLASS_DISABLED);
        atcButton.removeAttribute('disabled');
        atcButtonLabel.innerText = STR_ADD;
      }
    } else {
      this.disabled = true;
      atcButton.classList.add(CLASS_DISABLED);
      atcButton.setAttribute('disabled', 'disabled');
      atcButtonLabel.innerText = STR_SOLD_OUT;
    }
  }

  async addProduct(evt: Event) {
    evt.preventDefault();
    if (this.disabled || this.pending || !this.variantId || !this.variant)
      return;
    let { inventory_quantity, preorder_message } = this.variant;
    const { inventory_policy } = this.variant;
    if (!preorder_message) preorder_message = t('products.product.preorder');
    if (!inventory_quantity) inventory_quantity = 0;

    // Check inventory policy, if continue && inventory qty <= 0
    const preorderProduct =
      inventory_quantity <= 0 && inventory_policy === 'continue';

    const atcButton = findTarget(evt, SELECTOR_ATC_BUTTON);
    if (!atcButton) return;

    const atcButtonLabel = atcButton.querySelector<HTMLElement>(
      SELECTOR_ATC_BUTTON_LABEL
    );
    if (!atcButtonLabel) return;

    // Disable button
    this.disabled = true;
    atcButton.setAttribute('disabled', 'disabled');
    atcButton.classList.add(CLASS_DISABLED);

    const item = { id: this.variantId, quantity: 1, properties: {} };

    if (preorderProduct) {
      item.properties = {
        preorder: preorder_message,
      };
    }

    // Attempt to add to cart
    try {
      // Start pending
      this.pending = true;
      atcButton.classList.add(CLASS_PENDING);
      atcButtonLabel.innerText = STR_PENDING;

      await cartAdd({ items: [item] });

      atcButtonLabel.innerText = STR_ADDED;
    } catch (err: any) {
      const shpfErr = shopifyAjaxErrorGet(err);
      if (shpfErr === 'ERROR_ALL_IN_CART') {
        this.updateButton(err);
      } else {
        atcButtonLabel.innerText = STR_ERROR;
      }
      console.error(err);
    }

    document.dispatchEvent(new Event(ON_CART_FINISHED));

    await new Promise((resolve) => setTimeout(resolve, COOLDOWN_ADDED));

    // Stop pending
    this.pending = false;
    atcButton.classList.remove(CLASS_PENDING);
    this.updateButton(evt);
    this.updateHeaderCart();
  }

  /**
   * Updates cart count in header
   */
  updateHeaderCart = () => {
    const countElement =
      document.querySelector<HTMLElement>(SELECTOR_CART_COUNT);
    if (!countElement) return;

    const count = getCurrentCart().item_count;

    countElement.innerHTML = `${count}`;

    if (count > 0) {
      countElement.classList.add(CLASS_VISIBLE);
    } else {
      countElement.classList.remove(CLASS_VISIBLE);
    }
  };
}

// Initialise thumbnails
export const initialiseThumbnails = () => {
  document
    .querySelectorAll<HTMLElement>(SELECTOR_THUMBNAIL)
    .forEach((thumbnail) => {
      if (thumbnail.classList.contains(CLASS_INITIALISED)) return;
      thumbnail.classList.add(CLASS_INITIALISED);

      // What is this even?
      // eslint-disable-next-line no-new
      new ProductThumbnail(thumbnail);
    });
};

initialiseThumbnails();
